import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Divider,
  Container,
  Stack,
  Button,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import apiClient from "../../api/apiClient";
import { useSnackbar } from "../../context/SnackbarProvider";
import EditAdminDialog from "../../components/EditAdminDialog";
import EditRegistrationDialog from "../../components/EditRegistrationDialog";
import EventModal from "../../components/EventModal";
import ConfirmationDialog from "../../components/ConfirmationDialog";

const CardComponent = ({
  title,
  subtext,
  data = [],
  onRowClick,
  onEdit,
  onDelete,
  loading,
  selectedRowId,
}) => (
  <Card sx={{ boxShadow: 3, borderRadius: 2, overflow: "hidden" }}>
    <CardContent>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {subtext} ({data.length})
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      ) : data.length > 0 ? (
        data.map((item, index) => (
          <React.Fragment key={item._id}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "60px",
                py: 1,
                px: 2,
                cursor: "pointer",
                bgcolor:
                  selectedRowId === item._id ? "action.selected" : "inherit",
                "&:hover": {
                  bgcolor: "action.hover",
                  ".hover-icons": { display: "flex" },
                },
              }}
              onClick={() => onRowClick && onRowClick(item)}
            >
              <Typography>
                {item.username ||
                  item.name ||
                  item.employeeName ||
                  item.fullName}
              </Typography>
              <Box
                className="hover-icons"
                sx={{
                  display: "none",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item);
                  }}
                  sx={{ color: "primary.main" }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item._id);
                  }}
                  sx={{ color: "error.main" }}
                >
                  <Delete />
                </IconButton>
              </Box>
            </Box>
            {index < data.length - 1 && <Divider />}
          </React.Fragment>
        ))
      ) : (
        <Typography variant="body2" color="textSecondary">
          No records available.
        </Typography>
      )}
    </CardContent>
  </Card>
);

const SuperAdminDashboard = () => {
  const [admins, setAdmins] = useState([]);
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);

  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);

  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [editDialog, setEditDialog] = useState({
    open: false,
    type: "",
    data: null,
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    onConfirm: () => {},
  });
  const showSnackbar = useSnackbar();

  useEffect(() => {
    const fetchAdmins = async () => {
      setLoadingAdmins(true);
      try {
        const response = await apiClient.get("/superadmin/admins");
        setAdmins(response.data);
      } catch (err) {
        showSnackbar(
          err.response?.data?.message || "Failed to fetch admins.",
          "error"
        );
      } finally {
        setLoadingAdmins(false);
      }
    };
    fetchAdmins();
  }, [showSnackbar]);

  const handleAdminClick = async (admin) => {
    setSelectedAdminId(admin._id);
    setLoadingEvents(true);
    setEvents([]);
    try {
      const response = await apiClient.get(
        `/superadmin/admins/${admin._id}/events`
      );
      setEvents(response.data);
      setRegistrations([]);
      setSelectedEventId(null);
    } catch (err) {
      showSnackbar("Failed to fetch events.", "error");
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleEventClick = async (event) => {
    setSelectedEventId(event._id);
    setLoadingRegistrations(true);
    setRegistrations([]);
    try {
      const response = await apiClient.get(
        `/superadmin/events/${event._id}/registrations`
      );
      setRegistrations(response.data.registrations);
    } catch (err) {
      showSnackbar("Failed to fetch registrations.", "error");
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const handleEdit = (item, type) => {
    if (type === "event") {
      setSelectedEvent(item);
      setModalOpen(true);
    } else {
      setEditDialog({ open: true, type, data: item });
    }
  };

  const fetchData = async () => {
    try {
      if (selectedAdminId) {
        const eventResponse = await apiClient.get(
          `/superadmin/admins/${selectedAdminId}/events`
        );
        setEvents(eventResponse.data);
      } else {
        setEvents([]);
      }

      if (selectedEventId) {
        const registrationResponse = await apiClient.get(
          `/superadmin/events/${selectedEventId}/registrations`
        );
        setRegistrations(registrationResponse.data.registrations);
      } else {
        setRegistrations([]);
      }
    } catch (err) {
      showSnackbar("Failed to fetch data.", "error");
    }
  };

  const handleSave = async (updatedData) => {
    try {
      const { type, data } = editDialog;
      let url = "";
      let dataToUpdate = {};

      switch (type) {
        case "admin":
          url = `/superadmin/admins/${data._id}`;
          dataToUpdate = {
            username: updatedData.username,
            password: updatedData.password,
          };
          break;
        case "registration":
          url = `/superadmin/registrations/${data._id}`;
          dataToUpdate = updatedData;
          break;
        case "event":
          url = `/superadmin/events/${data._id}`;
          dataToUpdate = updatedData;
          break;
        default:
          throw new Error("Invalid type");
      }
      await apiClient.put(url, dataToUpdate);
      showSnackbar(`${type} updated successfully.`, "success");
      await fetchData();
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || "Failed to save the changes.",
        "error"
      );
    } finally {
      setEditDialog({ open: false, type: "", data: null });
    }
  };

  const handleEventModalClose = () => {
    setModalOpen(false);
    setSelectedEvent(null);
    fetchData();
  };

  const handleDelete = (id, type) => {
    setConfirmDialog({
      open: true,
      onConfirm: async () => {
        try {
          let url = "";
          switch (type) {
            case "admin":
              url = `/superadmin/admins/${id}`;
              break;
            case "event":
              url = `/superadmin/events/${id}`;
              break;
            case "registration":
              url = `/superadmin/registrations/${id}`;
              break;
            default:
              throw new Error("Invalid type");
          }

          await apiClient.delete(url);
          showSnackbar(`${type} deleted successfully.`, "success");
          await fetchData();
        } catch (err) {
          showSnackbar(
            err.response?.data?.message || "Failed to delete the item.",
            "error"
          );
        } finally {
          setConfirmDialog({ open: false, onConfirm: () => {} });
        }
      },
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 6, mb: 6 }}>
      {/* ✅ Header Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Super Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage administrators, events, and registrations efficiently from
            one unified dashboard.
          </Typography>
        </Box>
      </Stack>

      {/* Divider */}
      <Divider sx={{ mb: 4 }} />
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6} md={4}>
          <CardComponent
            title="Admins"
            subtext="Manage Administrators"
            data={admins}
            loading={loadingAdmins}
            selectedRowId={selectedAdminId}
            onRowClick={handleAdminClick}
            onEdit={(item) => handleEdit(item, "admin")}
            onDelete={(id) => handleDelete(id, "admin")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CardComponent
            title="Events"
            subtext="Oversee and update all Events"
            data={events}
            loading={loadingEvents}
            selectedRowId={selectedEventId}
            onRowClick={handleEventClick}
            onEdit={(item) => handleEdit(item, "event")}
            onDelete={(id) => handleDelete(id, "event")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CardComponent
            title="Registrations"
            subtext="Review and manage Event Registrations"
            data={registrations}
            loading={loadingRegistrations}
            onEdit={(item) => handleEdit(item, "registration")}
            onDelete={(id) => handleDelete(id, "registration")}
          />
        </Grid>
      </Grid>

      {editDialog.type === "admin" && (
        <EditAdminDialog
          open={editDialog.open}
          data={editDialog.data}
          onClose={() => setEditDialog({ open: false, type: "", data: null })}
          onSave={handleSave}
        />
      )}

      {editDialog.type === "registration" && (
        <EditRegistrationDialog
          open={editDialog.open}
          data={editDialog.data}
          onClose={() => setEditDialog({ open: false, type: "", data: null })}
          onSave={handleSave}
        />
      )}

      <EventModal
        open={modalOpen}
        onClose={handleEventModalClose}
        event={selectedEvent}
        fetchEvents={fetchData}
        useSuperAdmin={true}
      />

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, onConfirm: () => {} })}
        onConfirm={confirmDialog.onConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to Delete?"
        confirmButtonText="Delete"
      />
    </Container>
  );
};

export default SuperAdminDashboard;
